/**
 * HintTooltip — info-icon with hover/focus tooltip.
 *
 * Drops next to a field or section label to give the WHY behind a setting
 * without bloating a form with always-visible help text. Renders as a small
 * `i` glyph and surfaces the hint via Fluent's `Tooltip` so keyboard +
 * screen-reader users see it too.
 *
 * Moved here from `apps/process-designer/inspector/shared/HintTooltip.tsx`
 * (Wave 7 E10 toolkit dedupe) so any app can reach for it — it is a generic
 * presentational primitive, not designer-specific.
 */
import { Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import { InfoRegular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.colorNeutralForeground3,
    cursor: 'help',
    marginLeft: '4px',
    width: '14px',
    height: '14px',
    ':hover': { color: tokens.colorBrandForeground1 },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      borderRadius: '50%',
    },
  },
});

export function HintTooltip({ text }: { text: string }) {
  const styles = useStyles();
  return (
    <Tooltip content={text} relationship="description" withArrow>
      <span className={styles.trigger} tabIndex={0} role="tooltip" aria-label={text}>
        <InfoRegular fontSize={14} />
      </span>
    </Tooltip>
  );
}
